import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import PasswordInput from "./PasswordInput";
import SuDatePicker from "./SuDatePicker";

/* -- Custom select dropdown ------------------- */
export function SuSelect({ value, onChange, options, placeholder = "Select", label, required = false, searchable = false }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");
  const [dropPos, setDropPos] = useState(null);
  const btnRef    = useRef(null);
  const searchRef = useRef(null);

  // Mobile breakpoint matches the CSS (desktop card / phone grids flip at
  // 700) and the keyboard handler, so the bottom sheet and the floating
  // card never disagree about whether we're "on mobile".
  const isMobile = () => typeof window !== "undefined" && window.innerWidth < 700;

  /* Position the portal dropdown so it always fits in the viewport:
   *   • Open below if there's room; flip above if cramped.
   *   • Cap max-height to the available space (min 120, max 320) so the
   *     list scrolls internally instead of spilling past the page edge.
   *
   * Anchor style: opening DOWN sets `top` (top grows toward viewport
   * bottom); opening UP sets `bottom` (bottom is fixed just above the
   * trigger, so the container grows upward from there as content
   * fills it). Earlier bug: flipping up by setting
   * `top = triggerTop - maxHeight` reserved the full max-height even
   * when content was tiny, making short dropdowns visually detach
   * from the trigger and float high on screen.
   */
  const openDropdown = () => {
    if (!btnRef.current) { setOpen(true); return; }
    const r = btnRef.current.getBoundingClientRect();
    const margin       = 4;
    const minHeight    = 120;
    const maxAllowed   = 320;
    const preferredH   = 280;
    const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;
    const below = viewportH - r.bottom - margin;
    const above = r.top - margin;
    const openUp = (below < preferredH && above > below) || below < minHeight;
    const maxHeight = Math.max(minHeight, Math.min(maxAllowed, openUp ? above : below));
    setDropPos({
      openUp,
      top:    openUp ? null                       : r.bottom + margin,
      bottom: openUp ? (viewportH - r.top + margin) : null,
      left:   r.left,
      width:  r.width,
      maxHeight,
    });
    setOpen(true);
  };

  // Recompute dropPos from the trigger's current bounding rect. Used
  // on scroll / resize / layout shifts so the dropdown tracks the
  // trigger instead of detaching. Mirrors the flip-up logic from
  // openDropdown so behaviour is consistent.
  const reposition = () => {
    if (!btnRef.current) return;
    // On mobile we render a bottom sheet that's fixed to the viewport
    // — its position doesn't depend on the trigger at all. The
    // resize event fired by the soft keyboard sliding up was kicking
    // the trigger out of viewport here and closing the sheet right
    // after open. Bail out before doing any of that on mobile.
    if (isMobile()) return;
    const r = btnRef.current.getBoundingClientRect();
    const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;

    // If the trigger is no longer visible, close — no useful place to anchor.
    if (r.bottom < 0 || r.top > viewportH) { setOpen(false); return; }

    const margin     = 4;
    const minHeight  = 120;
    const maxAllowed = 320;
    const preferredH = 280;
    const below = viewportH - r.bottom - margin;
    const above = r.top - margin;
    const openUp = (below < preferredH && above > below) || below < minHeight;
    const maxHeight = Math.max(minHeight, Math.min(maxAllowed, openUp ? above : below));
    setDropPos({
      openUp,
      top:    openUp ? null : r.bottom + margin,
      bottom: openUp ? (viewportH - r.top + margin) : null,
      left:   r.left,
      width:  r.width,
      maxHeight,
    });
  };

  useEffect(() => {
    if (!open) { setSearch(""); return; }
    // The bottom sheet and desktop dropdown both live in a portal at
    // document.body, outside this component's DOM tree — so the
    // close handler can't just check `btnRef.current.contains`,
    // it'd see every tap inside the portal as "outside" and slam
    // the dropdown shut on the first search-input touch. Walk the
    // ancestor chain to detect the portaled containers explicitly.
    const close    = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target?.closest && (e.target.closest(".su-sheet") || e.target.closest(".su-select-list"))) return;
      setOpen(false);
    };
    const onScroll = () => reposition();
    const onResize = () => reposition();
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onResize);
    // Don't auto-focus the search input on mobile — it pops the soft
    // keyboard, which fires resize/scroll and re-triggers all the
    // race conditions we're trying to avoid. Mobile users tap the
    // search field when they actually want to type.
    if (searchable && !isMobile()) setTimeout(() => searchRef.current?.focus(), 50);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line
  }, [open, searchable]);

  // Match by value first, then fall back to label (case-insensitive) so
  // legacy data ("Male", "ADMINISTRATIVE SUPPORT SUPERVISOR") still
  // renders the correct option on renewal even though we now store the
  // dropdown code.
  const normalized = value != null ? String(value).toLowerCase() : "";
  const chosen = options.find((o) => (typeof o === "string" ? o : o.value) === value)
    || options.find((o) => {
      if (!normalized) return false;
      if (typeof o === "string") return o.toLowerCase() === normalized;
      return String(o.label).toLowerCase() === normalized;
    });
  const displayLabel = chosen ? (typeof chosen === "string" ? chosen : chosen.label) : null;

  // If we matched by label but the stored value doesn't equal the option's
  // canonical `value`, sync it upward so subsequent saves persist the code.
  useEffect(() => {
    if (chosen && typeof chosen !== "string" && chosen.value !== value) {
      onChange(chosen.value);
    }
  }, [chosen, value, onChange]);

  const filtered = searchable && search
    ? options.filter((o) => {
        const l = typeof o === "string" ? o : o.label;
        return l.toLowerCase().includes(search.toLowerCase());
      })
    : options;

  const handleSelect = (v) => { onChange(v); setOpen(false); };

  // Reusable tick for the currently-selected option (clearer than relying
  // on colour alone, which is the venue's brand hue and varies per venue).
  const checkMark = (
    <svg className="su-select-check" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
  );

  const trigger = (
    <button
      ref={btnRef}
      type="button"
      className={`su-input su-select-btn${!displayLabel ? " placeholder" : ""}`}
      onClick={() => (open ? setOpen(false) : openDropdown())}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={label ? `${label}${displayLabel ? `: ${displayLabel}` : ""}` : undefined}
    >
      <span>{displayLabel || placeholder}</span>
      <svg className={`su-select-arrow${open ? " open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );

  /* ── Mobile bottom sheet (ANY dropdown on mobile) ──
   *   Non-searchable dropdowns on mobile were falling through to the
   *   desktop portal and getting cropped at the viewport edge when the
   *   trigger sat near the bottom. The sheet anchors to the bottom of
   *   the screen and is always fully visible. */
  const mobileSheet = open && isMobile() && createPortal(
    <div className="su-sheet-backdrop" onMouseDown={() => setOpen(false)}>
      <div className="su-sheet" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="su-sheet-handle" />
        <div className="su-sheet-header">
          <span className="su-sheet-title">{label || "Select an option"}</span>
          <button type="button" className="su-sheet-close" onClick={() => setOpen(false)} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        {searchable && (
          <div className="su-sheet-search-row">
            <input
              ref={searchRef}
              type="text"
              className="su-sheet-search-input"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        <div className="su-sheet-list">
          {!search && (
            <div className={`su-select-opt is-clear${!value ? " selected" : ""}`} onMouseDown={() => handleSelect("")}>
              <span>{placeholder}</span>
              {!value && checkMark}
            </div>
          )}
          {filtered.map((o) => {
            const v = typeof o === "string" ? o : o.value;
            const l = typeof o === "string" ? o : o.label;
            return (
              <div key={v} className={`su-select-opt${v === value ? " selected" : ""}`} onMouseDown={() => handleSelect(v)}>
                <span>{l}</span>
                {v === value && checkMark}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );

  /* ── Desktop portal dropdown ── */
  const desktopList = open && !isMobile() && dropPos && createPortal(
    <div
      className="su-select-list"
      style={{
        position:  "fixed",
        // Either top or bottom is set, never both — bottom anchors the
        // container just above the trigger when we've flipped up, so a
        // short dropdown sits flush against the trigger instead of
        // floating at the top of the reserved max-height area.
        ...(dropPos.openUp ? { bottom: dropPos.bottom } : { top: dropPos.top }),
        left:      dropPos.left,
        width:     dropPos.width,
        maxHeight: dropPos.maxHeight,
        overflowY: "auto",
        zIndex:    9999,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {searchable && (
        <div className="su-select-search" style={{ position: "sticky", top: 0, background: "inherit", zIndex: 1 }}>
          <input
            ref={searchRef}
            type="text"
            className="su-select-search-input"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}
      {!search && (
        <div className={`su-select-opt is-clear${!value ? " selected" : ""}`} onMouseDown={() => handleSelect("")}>
          <span>{placeholder}</span>
          {!value && checkMark}
        </div>
      )}
      {filtered.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return (
          <div key={v} className={`su-select-opt${v === value ? " selected" : ""}`} onMouseDown={() => handleSelect(v)}>
            <span>{l}</span>
            {v === value && checkMark}
          </div>
        );
      })}
    </div>,
    document.body
  );

  return (
    <div className={`su-select-custom${open ? " open" : ""}`}>
      {trigger}
      {mobileSheet}
      {desktopList}
      {required && (
        <input
          tabIndex={-1}
          required
          readOnly
          value={value}
          style={{ opacity: 0, width: 0, height: 0, position: "absolute", pointerEvents: "none" }}
        />
      )}
    </div>
  );
}

const GOOGLE_MAPS_KEY = "AIzaSyD_RoRVNpR0hJdPgBcSpIMUqfsd2i3sMTM";

function loadGoogleMaps() {
  return new Promise((resolve) => {
    if (window.google?.maps?.places) { resolve(); return; }
    if (document.getElementById("gmaps-script")) {
      const check = setInterval(() => {
        if (window.google?.maps?.places) { clearInterval(check); resolve(); }
      }, 100);
      return;
    }
    const script = document.createElement("script");
    script.id = "gmaps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
    script.onload = resolve;
    document.head.appendChild(script);
  });
}

function parseComponents(components) {
  const get = (type, short = false) => {
    const c = components.find((x) => x.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : "";
  };
  // subpremise = unit / apartment number (e.g. "3" in "3/45 Grove Ave").
  // Preserving it keeps the street address meaningful without relying on
  // Google's `formatted_address`, which includes "Suburb, STATE, Country"
  // and would pollute the field.
  const unit   = get("subpremise");
  const num    = get("street_number");
  const route  = get("route");
  const street = [num, route].filter(Boolean).join(" ");
  const streetAddress = unit && street ? `${unit}/${street}` : street;
  return {
    streetAddress,
    suburb:   get("locality") || get("sublocality_level_1"),
    state:    get("administrative_area_level_1", true),
    postCode: get("postal_code"),
  };
}

const EMPTY = {
  title: "", firstName: "", lastName: "", email: "",
  dateOfBirth: "", gender: "", telephone: "", mobile: "",
  streetAddress: "", suburb: "", state: "", postCode: "",
  occupation: "",
  // IGT identification fields
  identificationType: "", identificationNumber: "",
  identificationCountryId: "", identificationState: "",
  identificationExpiry: "",
  // Address country defaults to Australia (IGT countryId 36) because
  // the signup form is AU-locked (Google autocomplete restricts to AU
  // and the section header says "Australian Address"). No UI field —
  // the address country never differs from the venue's country in
  // practice, and asking for it next to the address confused the user.
  countryId: 36,
  // Optional member PIN (IGT new joins only — the provider encrypts it
  // and saves it on the member's card so the user can verify their
  // identity later via the PIN flow).
  pin: "", pinConfirm: "",
};

export default function StepDetails({ slug, initialData, isExisting, onSubmit, onBack, venue }) {
  const [form, setForm] = useState({ ...EMPTY, ...initialData });
  const [dobError, setDobError] = useState("");
  // Optional-PIN validation: { field: "pin" | "confirm", msg } when the
  // user tries to continue with an incomplete/mismatched PIN, else null.
  const [pinError, setPinError] = useState(null);
  const pinRef        = useRef(null);
  const pinConfirmRef = useRef(null);
  const [addrFilled, setAddrFilled] = useState(
    !!(initialData?.suburb || initialData?.state || initialData?.postCode)
  );
  const [occupations, setOccupations] = useState([]);
  const isIGT = venue?.provider_type === "igt";
  // Sandbox/test mode: DOB + Address stay mandatory-looking but aren't enforced,
  // and are auto-filled with fixed test values if left blank (see handleSubmit),
  // so testers/demos don't have to type them. Real (live) venues still require.
  const isSandbox = venue?.signup_mode === "sandbox";

  // IGT reference data
  const [idTypes, setIdTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [idStates, setIdStates] = useState([]);
  const [igtTitles, setIgtTitles]   = useState([]);
  const [igtGenders, setIgtGenders] = useState([]);

  useEffect(() => {
    if (!isIGT || !slug) return;
    fetch(`/api/public/${slug}/identification-types`).then(r => r.json()).then(d => { if (Array.isArray(d)) setIdTypes(d); }).catch(() => {});
    fetch(`/api/public/${slug}/countries`).then(r => r.json()).then(d => {
      if (!Array.isArray(d)) return;
      // Pin AU + NZ to the top of the list — almost every member at
      // these venues holds an AU or NZ-issued ID, and scrolling past
      // 200+ countries on mobile is painful. Rest stays in the order
      // IGT returned (alphabetical by name).
      const isAu = (c) => /^australia$/i.test(c.name || "");
      const isNz = (c) => /^new zealand$/i.test(c.name || "");
      const au = d.find(isAu);
      const nz = d.find(isNz);
      const rest = d.filter((c) => !isAu(c) && !isNz(c));
      setCountries([au, nz, ...rest].filter(Boolean));
    }).catch(() => {});
    // Titles + genders come from cached IGT ref data so the values the
    // user picks carry the same IDs IGT expects in the member-create
    // body (titleId is a number; genderId is "M"/"F"/etc).
    fetch(`/api/public/${slug}/titles`).then(r => r.json()).then(d => { if (Array.isArray(d)) setIgtTitles(d); }).catch(() => {});
    fetch(`/api/public/${slug}/genders`).then(r => r.json()).then(d => { if (Array.isArray(d)) setIgtGenders(d); }).catch(() => {});
  }, [isIGT, slug]);

  useEffect(() => {
    if (!isIGT || !form.identificationCountryId || !slug) { setIdStates([]); return; }
    fetch(`/api/public/${slug}/countries/${form.identificationCountryId}/states`)
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setIdStates(d); }).catch(() => setIdStates([]));
  }, [isIGT, form.identificationCountryId, slug]);

  useEffect(() => {
    if (!slug) return;
    fetch(`/api/public/${slug}/occupations`)
      .then((r) => r.json())
      .then((rows) => {
        if (Array.isArray(rows)) {
          setOccupations(
            rows.map((r) => ({ value: r.occupation_code, label: r.occupation_desc }))
          );
        }
      })
      .catch(() => {}); // silently fall back to empty list
  }, [slug]);

  const addrRef = useRef(null);
  const acRef   = useRef(null);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setField = (field) => (value) => setForm((p) => ({ ...p, [field]: value }));

  useEffect(() => {
    loadGoogleMaps().then(() => {
      if (!addrRef.current || acRef.current) return;
      const ac = new window.google.maps.places.Autocomplete(addrRef.current, {
        types: ["address"],
        componentRestrictions: { country: "au" },
        fields: ["address_components"],
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place?.address_components) return;
        const parsed = parseComponents(place.address_components);
        // Use the parsed street only. Google's autocomplete sets the
        // input's `.value` to the full formatted address
        // ("… Narwee NSW, Australia") — we don't want that leaking
        // into the Street Address field since state / postcode /
        // country are captured separately.
        setForm((prev) => ({ ...prev, ...parsed }));
        setAddrFilled(true);
      });
      acRef.current = ac;
    });
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // DOB is mandatory for real signups; sandbox auto-fills it below.
    if (!isSandbox && !form.dateOfBirth) {
      setDobError("Please select your date of birth.");
      return;
    }
    setDobError("");
    // Entering a street address expands the suburb/state/postcode fields.
    if (form.streetAddress && !addrFilled) {
      setAddrFilled(true);
      return;
    }
    // Validate optional PIN: must be 4 digits and match confirmation when
    // anything is entered. Empty is fine (user chose to skip). On failure
    // we flag the offending field (red outline + message) and focus it —
    // previously this returned silently, so the Continue button looked dead.
    if (form.pin || form.pinConfirm) {
      if (!/^\d{4}$/.test(form.pin)) {
        setPinError({ field: "pin", msg: "Enter a 4-digit PIN." });
        pinRef.current?.focus();
        return;
      }
      if (form.pin !== form.pinConfirm) {
        setPinError({
          field: "confirm",
          msg: form.pinConfirm ? "PINs don't match." : "Please re-enter your PIN to confirm it.",
        });
        pinConfirmRef.current?.focus();
        return;
      }
    }
    setPinError(null);
    // Don't leak the confirm field to the backend.
    const { pinConfirm, ...payload } = form;
    // Sandbox convenience: fill any left-blank mandatory Address/DOB with fixed
    // test values so testers/demos aren't forced to type them. Never runs live.
    if (isSandbox) {
      if (!payload.dateOfBirth)  payload.dateOfBirth  = "1990-01-01";
      if (!payload.streetAddress) payload.streetAddress = "1 Test Street";
      if (!payload.suburb)        payload.suburb        = "Sydney";
      if (!payload.state)         payload.state         = "NSW";
      if (!payload.postCode)      payload.postCode      = "2000";
    }
    onSubmit(payload);
  };

  return (
    <>
      <h2 className="step-title">Personal Details</h2>
      <p className="step-sub">
        {isExisting
          ? "We found your record - review and update as needed."
          : "Tell us a little about yourself."}
      </p>

      {isExisting && (
        <div className="su-alert info">Details pre-filled from your membership</div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Name row */}
        <div className="su-grid-3">
          <div className="su-field">
            <label className="su-label">Title</label>
            <SuSelect
              label="Title"
              placeholder="Title"
              value={form.title}
              onChange={setField("title")}
              options={isIGT && igtTitles.length > 0
                ? igtTitles.map((t) => ({ value: t.title_id, label: t.name }))
                : ["Mr", "Mrs", "Ms", "Dr"]}
            />
          </div>
          <div className="su-field">
            <label className="su-label">First Name <span className="su-required">*</span></label>
            <input className="su-input" type="text" value={form.firstName}
              onChange={set("firstName")} required maxLength={50} placeholder="First name" />
          </div>
          <div className="su-field">
            <label className="su-label">Last Name <span className="su-required">*</span></label>
            <input className="su-input" type="text" value={form.lastName}
              onChange={set("lastName")} required maxLength={50} placeholder="Last name" />
          </div>
        </div>

        {/* Email + DOB */}
        <div className="su-grid-2">
          <div className="su-field">
            <label className="su-label">Email <span className="su-required">*</span></label>
            <input className="su-input" type="email" value={form.email}
              onChange={set("email")} required maxLength={100} placeholder="email@example.com" />
          </div>
          <div className="su-field">
            <label className="su-label">Date of Birth <span className="su-required">*</span></label>
            <SuDatePicker
              label="Date of Birth"
              value={form.dateOfBirth}
              onChange={(iso) => { setForm((p) => ({ ...p, dateOfBirth: iso })); setDobError(""); }}
              required={!isSandbox}
              min="1900-01-01"
              max={new Date().toISOString().slice(0, 10)}
            />
            {dobError && <span className="su-field-error">{dobError}</span>}
          </div>
        </div>

        {/* Gender + Mobile — Gender is hidden when the venue disables it
            (optional at the provider); Mobile then takes the full row. */}
        {venue?.gender_field_enabled !== false ? (
          <div className="su-grid-2">
            <div className="su-field">
              <label className="su-label">Gender</label>
              <SuSelect
                label="Gender"
                placeholder="Select gender"
                value={form.gender}
                onChange={setField("gender")}
                options={isIGT && igtGenders.length > 0
                  ? igtGenders.map((g) => ({ value: g.gender_id, label: g.name }))
                  : [
                      { value: "M", label: "Male" },
                      { value: "F", label: "Female" },
                      { value: "X", label: "Prefer not to say" },
                    ]}
              />
            </div>
            <div className="su-field">
              <label className="su-label">Mobile</label>
              <input className="su-input" type="tel" value={form.mobile}
                onChange={set("mobile")} maxLength={20} placeholder="04xx xxx xxx" />
            </div>
          </div>
        ) : (
          <div className="su-field">
            <label className="su-label">Mobile</label>
            <input className="su-input" type="tel" value={form.mobile}
              onChange={set("mobile")} maxLength={20} placeholder="04xx xxx xxx" />
          </div>
        )}

        <div className="su-field">
          <label className="su-label">Telephone</label>
          <input className="su-input" type="tel" value={form.telephone}
            onChange={set("telephone")} maxLength={20} placeholder="(02) xxxx xxxx" />
        </div>

        <div className="su-divider">Australian Address</div>

        <div className="su-field">
          <label className="su-label">Street Address <span className="su-required">*</span></label>
          <input
            ref={addrRef}
            className="su-input"
            type="text"
            value={form.streetAddress}
            onChange={set("streetAddress")}
            required={!isSandbox}
            maxLength={200}
            placeholder="Start typing your address..."
            autoComplete="off"
          />
          {!addrFilled && (
            <button type="button" className="su-addr-manual-link"
              onClick={() => setAddrFilled(true)}>
              Enter address manually
            </button>
          )}
        </div>

        {addrFilled && (
          <div className="su-addr-fields su-grid-3">
            <div className="su-field">
              <label className="su-label">Suburb <span className="su-required">*</span></label>
              <input className="su-input" type="text" value={form.suburb}
                onChange={set("suburb")} required={!isSandbox} maxLength={100} placeholder="Suburb" />
            </div>
            <div className="su-field">
              <label className="su-label">State <span className="su-required">*</span></label>
              <SuSelect
                label="State"
                placeholder="Select state"
                value={form.state}
                onChange={setField("state")}
                options={["NSW","VIC","QLD","SA","WA","TAS","ACT","NT"]}
                required={!isSandbox}
              />
            </div>
            <div className="su-field">
              <label className="su-label">Postcode <span className="su-required">*</span></label>
              <input className="su-input" type="text" value={form.postCode}
                onChange={set("postCode")} required={!isSandbox} maxLength={4}
                pattern="\d{4}" placeholder="2000" inputMode="numeric" />
            </div>
          </div>
        )}

        <div className="su-field">
          <label className="su-label">Occupation</label>
          {occupations.length > 0 ? (
            <SuSelect
              label="Occupation"
              value={form.occupation}
              onChange={setField("occupation")}
              options={occupations}
              placeholder="Select occupation"
              searchable
            />
          ) : (
            <input className="su-input" type="text" value={form.occupation}
              onChange={set("occupation")} maxLength={100} placeholder="e.g. Engineer" />
          )}
        </div>

        {/* Optional PIN — IGT new joins only. Renewals keep their
            existing PIN, so we only surface this when the user is NOT
            in the existing-member flow. */}
        {isIGT && !isExisting && (
          <>
            <div className="su-divider">Set a PIN (optional)</div>
            <p className="step-sub" style={{ marginTop: 0 }}>
              Pick a 4-digit PIN now so you can verify your identity for
              future wallet-pass downloads or renewals. You can skip
              this and set one later at the venue.
            </p>
            <div className="su-grid-2">
              <div className="su-field">
                <label className="su-label">PIN</label>
                <PasswordInput
                  ref={pinRef}
                  className={`su-input${pinError?.field === "pin" ? " su-input-error" : ""}`}
                  inputMode="numeric"
                  pattern="\d{4}"
                  maxLength={4}
                  value={form.pin}
                  onChange={(e) => { setForm((p) => ({ ...p, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })); setPinError(null); }}
                  placeholder="••••"
                  autoComplete="new-password"
                />
              </div>
              <div className="su-field">
                <label className="su-label">Confirm PIN</label>
                <PasswordInput
                  ref={pinConfirmRef}
                  className={`su-input${pinError?.field === "confirm" ? " su-input-error" : ""}`}
                  inputMode="numeric"
                  pattern="\d{4}"
                  maxLength={4}
                  value={form.pinConfirm}
                  onChange={(e) => { setForm((p) => ({ ...p, pinConfirm: e.target.value.replace(/\D/g, "").slice(0, 4) })); setPinError(null); }}
                  placeholder="••••"
                  autoComplete="new-password"
                />
              </div>
            </div>
            {pinError && <div className="su-field-error">{pinError.msg}</div>}
          </>
        )}

        {/* Identification – IGT venues only */}
        {isIGT && (
          <>
            <div className="su-divider">Identification</div>

            <div className="su-grid-2">
              <div className="su-field">
                <label className="su-label">ID Type <span className="su-required">*</span></label>
                <SuSelect
                  label="ID Type"
                  value={form.identificationType}
                  onChange={setField("identificationType")}
                  options={idTypes.map((t) => ({ value: t.identification_type_id, label: t.name }))}
                  placeholder="Select ID type"
                  required
                />
              </div>
              <div className="su-field">
                {/* Label + placeholder follow the chosen ID type. Drivers
                    licences and passports are the common cases — fall
                    back to a generic "ID Number" for anything else
                    (e.g. proof-of-age cards) so we don't mislabel. */}
                {(() => {
                  const selectedIdType = idTypes.find(
                    (t) => String(t.identification_type_id) === String(form.identificationType)
                  );
                  const name = selectedIdType?.name || "";
                  const isPassport = /passport/i.test(name);
                  const isLicence  = /licen[cs]e|driver/i.test(name);
                  const label = isPassport ? "Passport Number"
                              : isLicence  ? "Licence Number"
                              : name ? `${name} Number` : "ID Number";
                  const placeholder = isPassport ? "e.g. PA1234567" : "e.g. DL12345678";
                  return (
                    <>
                      <label className="su-label">{label} <span className="su-required">*</span></label>
                      <input className="su-input" type="text" value={form.identificationNumber}
                        onChange={set("identificationNumber")} required maxLength={50}
                        placeholder={placeholder} />
                    </>
                  );
                })()}
              </div>
            </div>

            {/* su-id-grid: opts out of the generic 90px-first-column
                mobile rule used by Title/First Name/Last Name — these
                three fields all need a proper share of the row, not a
                squashed 90px Country column. */}
            <div className="su-grid-3 su-id-grid">
              <div className="su-field">
                <label className="su-label">ID Country</label>
                <SuSelect
                  label="ID Country"
                  value={form.identificationCountryId}
                  onChange={setField("identificationCountryId")}
                  options={countries.map((c) => ({ value: c.country_id, label: c.name }))}
                  placeholder="Select country"
                  searchable
                />
              </div>
              <div className="su-field">
                <label className="su-label">ID State</label>
                <SuSelect
                  label="ID State"
                  value={form.identificationState}
                  onChange={setField("identificationState")}
                  options={idStates.map((s) => ({ value: s.state_id, label: s.name }))}
                  placeholder="Select state"
                  searchable
                />
              </div>
              <div className="su-field">
                <label className="su-label">Expiry Date</label>
                <SuDatePicker
                  label="Expiry Date"
                  value={form.identificationExpiry}
                  onChange={(iso) => setForm(p => ({ ...p, identificationExpiry: iso }))} />
              </div>
            </div>
          </>
        )}

        <div className="su-btn-row">
          <button type="button" className="su-btn su-btn-outline" onClick={onBack}>Back</button>
          <button type="submit" className="su-btn su-btn-primary">Continue</button>
        </div>
      </form>
    </>
  );
}